import * as z from 'zod/v4';

import { biExpressionSchema } from './expression';
import { tableReferenceSchema } from './tableReference';

const joinTypeSchema = z.enum(['INNER', 'LEFT', 'RIGHT', 'FULL']);
/** Join type */
export type JoinType = z.infer<typeof joinTypeSchema>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const JoinType: { [T in JoinType]: T } = joinTypeSchema.enum;

export const UNION = 'UNION';
const unionTypeSchema = z.literal(UNION);
export type UnionType = z.infer<typeof unionTypeSchema>;

const joinSpecSchema = z.union([
  z.object({
    left: tableReferenceSchema,
    right: tableReferenceSchema,
    joinType: joinTypeSchema,
    on: z.array(biExpressionSchema).readonly(),
  }),
  z.object({
    left: tableReferenceSchema,
    right: tableReferenceSchema,
    joinType: unionTypeSchema,
  }),
]);

/** An object defining a SQL-style join or union between two queries */
export type JoinSpec = z.infer<typeof joinSpecSchema>;

const sortDirectionSchema = z.enum(['ASC', 'DESC']);
/** Join type */
export type SortDirection = z.infer<typeof sortDirectionSchema>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SortDirection: { [T in SortDirection]: T } =
  sortDirectionSchema.enum;

export const biQueryInputSchema = z.object({
  select: z.array(biExpressionSchema).readonly(),
  // Making the arrays nullish so that they are compatible with the GQL types (that allow both null & undefined)
  groupBy: z.array(biExpressionSchema).readonly().nullish(),
  where: z.array(biExpressionSchema).readonly().nullish(),
  order: z.array(biExpressionSchema).readonly().nullish(),
  limit: z.number().nonnegative().int().nullish(),
  offset: z.number().nonnegative().int().nullish(),
});

const biTableQuerySchema = biQueryInputSchema.extend({
  from: z.union([tableReferenceSchema, joinSpecSchema]),
});

/** A SQL-style query for the BI query service, defining a single table */
export type BiTableQuery = z.infer<typeof biTableQuerySchema>;

const aliasedBiTableQuerySchema = biTableQuerySchema.extend({
  alias: z.string(),
});

/** BI table query with a required alias */
export type AliasedBiTableQuery = z.infer<typeof aliasedBiTableQuerySchema>;

/** Input type for BI query formulation (aka a "flat" query) */
export type BiQueryInput = Omit<BiTableQuery, 'from'>;

// Explicit type annotation is needed to avoid errors: `The inferred type of
// this node exceeds the maximum length the compiler will serialize`
export const biQuerySchema: z.ZodType<{
  tables: ReadonlyArray<AliasedBiTableQuery>;
  query: BiTableQuery;
}> = z.object({
  tables: z.array(aliasedBiTableQuerySchema).readonly(),
  query: biTableQuerySchema,
});

/**
 * A SQL-style query for the BI query service, defining a top-level query
 * and a list of CTE dependencies
 */
export type BiQuery = z.infer<typeof biQuerySchema>;
